import {
  aiEditsRecipeGenerateRecipeRetrieve,
  aiEditsRecipePartialUpdate,
  aiEditsRecipeDestroy,
  aiEditsRecipeTagsRetrieve,
  aiEditsRecipeList,
  aiEditsRecipeGetRecommendedRecipeRetrieve
} from '@goldcast/api/content';
import type { RecipeData, AgenticContext, Recipe } from 'agentic_chat/types';
import { CACHE_KEYS, setInCache } from '../cache';
import { mapRecipeToAgenticChat } from '../constants';
import type { AIRecipe } from '../types';
import { showSuccessToast, showErrorToast } from '@/libs/toast/toast';

/**
 * Generate a new recipe from the current agent's successful edits.
 *
 * @param context - The current context (must contain chatSessionId in metadata)
 * @returns Promise resolving to the generated recipe data
 * @throws Error if chatSessionId is missing or recipe generation fails
 */
export async function generateRecipe(context: AgenticContext): Promise<RecipeData> {
  const chatSessionId = context.metadata.chatSessionId as string;
  if (!chatSessionId) {
    const error = new Error('chatSessionId is required in context.metadata for recipe generation');
    window?.logger?.error('AIEditsAdapter:', error.message);
    throw error;
  }

  try {
    const recipeResponse = await aiEditsRecipeGenerateRecipeRetrieve({
      queryParams: { ai_edits_id: chatSessionId }
    });

    if (!recipeResponse) {
      throw new Error('No response from recipe generation API');
    }

    return {
      id: recipeResponse.id,
      recipeName: recipeResponse.recipe_name,
      tags: recipeResponse.tags || [],
      prompt: recipeResponse.recipe
    };
  } catch (error) {
    window?.logger?.error('AIEditsAdapter: Error generating recipe:', error);
    showErrorToast('Failed to generate recipe. Please try again.');
    throw error;
  }
}

export async function updateRecipe(recipeId: string, data: RecipeData): Promise<void> {
  if (!recipeId) {
    const error = new Error('recipeId is required for recipe update');
    window?.logger?.error('AIEditsAdapter:', error.message);
    throw error;
  }

  try {
    await aiEditsRecipePartialUpdate({
      id: recipeId,
      body: {
        recipe_name: data.recipeName,
        tags: data.tags,
        recipe: data.prompt
      }
    });

    showSuccessToast('Recipe saved successfully!');
  } catch (error) {
    window?.logger?.error('AIEditsAdapter: Error updating recipe:', error);
    showErrorToast('Failed to save recipe. Please try again.');
    throw error;
  }
}

export async function deleteRecipe(recipeId: string): Promise<void> {
  if (!recipeId) {
    const error = new Error('recipeId is required for recipe deletion');
    window?.logger?.error('AIEditsAdapter:', error.message);
    throw error;
  }

  try {
    await aiEditsRecipeDestroy({ id: recipeId });

    showSuccessToast('Recipe deleted successfully');
  } catch (error) {
    window?.logger?.error('AIEditsAdapter: Error deleting recipe:', error);
    showErrorToast('Failed to delete recipe');
    throw error;
  }
}

export async function getRecipeTags(): Promise<string[]> {
  try {
    const response = await aiEditsRecipeTagsRetrieve({});

    if (!response || !response.tags) {
      window?.logger?.warn('AIEditsAdapter: No tags returned from API');
      return [];
    }

    return response.tags;
  } catch (error) {
    window?.logger?.error('AIEditsAdapter: Error fetching recipe tags:', error);
    return [];
  }
}

export async function getRecipes(_context: AgenticContext): Promise<Recipe[]> {
  try {
    const response = await aiEditsRecipeList({
      queryParams: {
        limit: 100,
        offset: 0
      }
    });

    const results = response.results;
    if (Array.isArray(results)) {
      const recipes = (results as unknown as AIRecipe[]).map(mapRecipeToAgenticChat);
      setInCache(CACHE_KEYS.recipes, recipes);
      return recipes;
    }

    const emptyRecipes: Recipe[] = [];
    setInCache(CACHE_KEYS.recipes, emptyRecipes);
    return emptyRecipes;
  } catch (error) {
    window?.logger?.error('AIEditsAdapter: Error fetching recipes:', error);
    return [];
  }
}

export async function getRecommendedRecipe(context: AgenticContext): Promise<string[] | null> {
  const clipId = context.metadata.clipId as string;
  if (!clipId) {
    throw new Error('clipId is required in context.metadata');
  }

  try {
    const apiParams = {
      queryParams: { asset: clipId }
    };

    const recommendedRecipeResponse = await aiEditsRecipeGetRecommendedRecipeRetrieve(apiParams);

    if (recommendedRecipeResponse && (recommendedRecipeResponse as any).recipe_ids) {
      const recipeIds = (recommendedRecipeResponse as any).recipe_ids as string[];

      if (Array.isArray(recipeIds) && recipeIds.length > 0) {
        setInCache(CACHE_KEYS.recommendedRecipe(clipId), recipeIds);
        return recipeIds;
      }
    }

    setInCache(CACHE_KEYS.recommendedRecipe(clipId), null);
    return null;
  } catch (error) {
    window?.logger?.error('AIEditsAdapter: Error fetching recommended recipe:', error);
    setInCache(CACHE_KEYS.recommendedRecipe(clipId), null);
    return null;
  }
}
